import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { OfferType, useOffers, usePlanOffers, useSession } from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { UpsellSupportSeveralSelection } from './UpsellSupportSeveralSelection';
import {
  Button,
  InformationBox,
  UpsellStudyModal,
  useBoolModalState,
} from 'components';
import { filterOffers } from 'pages/StudySetup/GeneralSetup/filterOffers';

interface Props {
  sessionId?: string;
  className?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.marginBase,
    marginTop: theme.marginBase,
  },
}));

export const UpsellSupportReviewSection = ({ sessionId, className }: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [modalOpened, openModal, closeModal] = useBoolModalState();
  const { data: allOffers } = useOffers();

  const { data: session } = useSession(sessionId);
  const { data: planOffers } = usePlanOffers(session?.planId);

  const supportOffer = useMemo(() => {
    return planOffers?.find((offer) => offer.type === OfferType.SUPPORT);
  }, [planOffers]);

  const freeOffer = supportOffer?.prices?.eur === 0;
  const name = supportOffer?.name;

  const availableOffers = useMemo(() => {
    if (!session || !allOffers) return [];
    return filterOffers(session, allOffers);
  }, [session, allOffers]);

  if (!supportOffer || !availableOffers.length) {
    return null;
  }

  return freeOffer ? (
    <UpsellSupportSeveralSelection
      sessionId={sessionId}
      className={className}
    />
  ) : (
    <InformationBox variant="success" noIcon className={className}>
      <Trans
        i18nKey="editSession.review.upsell.selected"
        values={{ name }}
        components={{
          b: <strong />,
        }}
      />
      <div className={classes.buttons}>
        <Button
          type="button"
          color="green"
          variant="muted"
          small
          onClick={openModal}
        >
          {t('editSession.review.upsell.change')}
        </Button>
      </div>
      <UpsellStudyModal
        isOpen={modalOpened}
        onRequestClose={closeModal}
        sessionId={sessionId}
      />
    </InformationBox>
  );
};
